// AddSurveyQuestions.js
import React from "react";
import {
  Box,
  Button,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  IconButton,
  Grid2,
  MenuItem,
  Select,
  InputLabel,
  FormControl,
  FormControlLabel,
  Switch,
  Typography,
  Divider,
  Paper,
  Stack,
  Tooltip,
} from "@mui/material";
import { Formik, Form, FieldArray } from "formik";
import * as Yup from "yup";
import {
  Add as AddIcon,
  Delete as DeleteIcon,
  ArrowUpward,
  ArrowDownward,
  Close as CloseIcon,
} from "@mui/icons-material";
import API from "../../api/api";
import { useDispatch } from "react-redux";
import { setSuccessSnack } from "../../Reducers/snackSuccess";
import { setSnackError } from "../../Reducers/snackErrors";

/**
 * Usage:
 * <AddSurvey onClose={(result) => { ... }} initialData={dataOrNull} open={open} />
 * - onClose(null) is called when user cancels/ closes without saving
 * - onClose(payload) is called on Submit with the survey + questions shape
 *
 * This component only builds/validates the data; you can POST it in the parent.
 */


const QUESTION_TYPES = [
    "short_text",
    "long_text",
    "multi_choice",
    "yes_no",
  ];
  

const SurveySchema = Yup.object({
  // Base survey
  title: Yup.string().trim().required("Title is required"),
  description: Yup.string().trim().max(4000, "Too long"),
  order_by: Yup.number().integer().min(1).default(1),
  is_active: Yup.boolean().required(),
  internal: Yup.boolean().default(false),

  // Questions
  questions: Yup.array()
    .of(
      Yup.object({
        id: Yup.number().notRequired(),
        question_text: Yup.string()
          .trim()
          .required("Question text is required"),
        question_type: Yup.mixed()
          .oneOf(QUESTION_TYPES, "Invalid type")
          .required("Type is required"),
        choices: Yup.array().of(
            Yup.object({
                choice_text: Yup.string()
            })
        ).min(0).max(4)
      })
    )
    .min(1, "Add at least one question"),
    remove_questions: Yup.array()
    .of(Yup.object({
      id: Yup.number()
    }))
});



const AddSurvey = ({ onClose, initialData, open }) => {

const dispatch = useDispatch()
  const initialValues = {
    id: initialData?.id  || null,
    title: initialData?.title || "",
    internal: initialData.internal || false,
    description: initialData?.description || "",
    order_by: initialData?.order_by || 1,
    is_active: initialData?.is_active || "",
    questions: initialData?.questions || [],
    remove_questions: []
  }

  const handleSubmit = async (values, actions) => {
    try {
      let response = null;
      if (values.id != null) {
        response = await API.post('/update_survey', values);
      } else {
        response = await API.post('/create_survey', values);
      }
      
      if (response.data.status === 'Updated') {
        dispatch(setSuccessSnack({title: 'Success', body: `Updated successfully`}));
        return;
      }
      if (response.data.status === 'OK') {
        dispatch(setSuccessSnack({
          title: 'Success', 
          body: `Created program successfully with id ${response.data.id}`
        }));
        return;
      }
    } catch(error) {
      console.log(error);
      if (error.isAuthError || error.isForbidden) {
        return;
      } 
      if (error.isNetworkError) {
        dispatch(setSnackError({ 
          title: 'Connection Error', 
          body: error.message 
        }));
      } else {
        dispatch(setSnackError({
          title: 'Error found',
          body: error.message || 'Unable to add Program.'
        }));
      }
      return;
    } finally {
        actions.setSubmitting(true);
        onClose();
    }
  };

  return (
    <Dialog
      open={open}
      onClose={() => onClose?.(null)}
      maxWidth="sm"
      fullWidth
    >
      <DialogTitle sx={{ display: "flex", alignItems: "center", pr: 6 }}>
        <Stack>
        <Typography variant="h6" fontWeight={'bold'}>
            {initialData && initialData.id ? "Edit Survey" : "Create Survey"}
        </Typography>
        <Typography variant="body2">
            Create pre, post surveys to gather sentiment between staff and students
        </Typography>
        </Stack>
      </DialogTitle>

      <Formik
        initialValues={initialValues}
        validationSchema={SurveySchema}
        onSubmit={(values, actions) => {
          actions.setSubmitting(true);
          handleSubmit(values, actions)
        }}
      >
        {({
          values,
          errors,
          touched,
          handleChange,
          handleBlur,
          isSubmitting,
          setFieldValue,
        }) => (
          <Form>
            <DialogContent dividers>
              {/* Survey header section */}
              <Typography variant="subtitle2" color="text.secondary" gutterBottom>
                Survey Details
              </Typography>
              <Grid2 container spacing={1} sx={{ mb: 2 }}>
                <Grid2 item size={{xs:12, md: 8}}>
                  <TextField
                    fullWidth
                    label="Title"
                    name="title"
                    value={values.title}
                    onChange={handleChange}
                    onBlur={handleBlur}
                    error={touched.title && Boolean(errors.title)}
                    helperText={touched.title && errors.title}
                  />
                </Grid2>
                <Grid2 item size={{xs:6, md:4}}>
                  <TextField
                    fullWidth
                    type="number"
                    label="Order By"
                    name="order_by"
                    inputProps={{ min: 1 }}
                    value={values.order_by}
                    onChange={(e) =>
                      setFieldValue("order_by", Number(e.target.value || 1))
                    }
                    onBlur={handleBlur}
                    error={touched.order_by && Boolean(errors.order_by)}
                    helperText={touched.order_by && errors.order_by}
                  />
                </Grid2>
                <Grid2 item size={{xs:12}}>
                  <TextField
                    fullWidth
                    multiline
                    minRows={2}
                    label="Description"
                    name="description"
                    value={values.description}
                    onChange={handleChange}
                    onBlur={handleBlur}
                    error={touched.description && Boolean(errors.description)}
                    helperText={touched.description && errors.description}
                  />
                </Grid2>
                <Grid2 item size={{xs:6, md:3}}>
                  <FormControlLabel
                    control={
                      <Switch
                        checked={values.is_active}
                        onChange={(e) =>
                          setFieldValue("is_active", e.target.checked)
                        }
                        name="is_active"
                        color="secondary"
                      />
                    }
                    label="Active"
                  />
                </Grid2>
                <Grid2 item size={{xs:6, md:3}}>
                 <Tooltip title={"New: Student facing surveys via QR coming soon"}>
                  <FormControlLabel
                      control={
                        <Switch
                          disabled={true}
                          checked={values.internal}
                          onChange={(e) =>
                            setFieldValue("internal", e.target.checked)
                          }
                          name="internal"
                          color="secondary"
                        />
                      }
                      label="Internal"
                    />
                 </Tooltip>
                </Grid2>
              </Grid2>

              <Divider sx={{ my: 2 }} />

              {/* Questions section */}
              <Stack direction="row" alignItems="center" spacing={1} sx={{ mb: 1 }}>
                <Typography variant="subtitle2" color="text.secondary">
                  Add Questions
                </Typography>
                <Tooltip title="Add question">
                  <IconButton
                    size="small"
                    onClick={() =>
                      setFieldValue("questions", [
                        ...values.questions,
                        { question_text: "", question_type: "short_text", choices: [] },
                      ])
                    }
                  >
                    <AddIcon fontSize="small" />
                  </IconButton>
                </Tooltip>
                <Typography variant="caption" color="text.secondary">
                  
                </Typography>
              </Stack>

              <FieldArray
                name="questions"
                render={(arrayHelpers) => (
                  <Stack spacing={1}>
                    {values.questions.map((q, index) => {
                      const qTextError =
                        touched.questions?.[index]?.question_text &&
                        errors.questions?.[index]?.question_text;
                      const qTypeError =
                        touched.questions?.[index]?.question_type &&
                        errors.questions?.[index]?.question_type;

                      return (
                        <Paper
                          key={`q-${index}`}
                          variant="outlined"
                          sx={{ p: 2 }}
                        >
                          <Grid2 container spacing={1} alignItems="center">
                            <Grid2 item size={{xs:12}}>
                              <TextField
                                fullWidth
                                label={`Question ${index + 1}`}
                                name={`questions[${index}].question_text`}
                                value={q.question_text}
                                onChange={handleChange}
                                rows={2}
                                onBlur={handleBlur}
                                error={Boolean(qTextError)}
                                helperText={qTextError}
                              />
                            </Grid2>

                            <Grid2 item size={{xs:12}}>
                              <FormControl fullWidth>
                                <InputLabel id={`qtype-${index}`}>Response type</InputLabel>
                                <Select
                                  labelId={`qtype-${index}`}
                                  label="Type"
                                  name={`questions[${index}].question_type`}
                                  value={q.question_type}
                                  onChange={handleChange}
                                  onBlur={handleBlur}
                                  error={Boolean(qTypeError)}
                                >
                                  {QUESTION_TYPES.map((t) => (
                                    <MenuItem key={t} value={t}>
                                      {t}
                                    </MenuItem>
                                  ))}
                                </Select>
                              </FormControl>

                              {
                                q.question_type === "multi_choice" ? 
                                <FormControl fullWidth sx={{pt: 1}}>
                                    <Stack spacing={1}>
                                    {
                                        [...Array(4)].map((item, idx) => (
                                            <TextField
                                                fullWidth
                                                size="small"
                                                label={`Choice ${idx + 1}`}
                                                value={q.choices[idx]?.choice_text || ''}
                                                onChange={(e) => setFieldValue(`questions[${index}].choices[${idx}].choice_text`, e.target.value)}
                                                />
                                        )) 
                                    }
                                    </Stack>
                                </FormControl>
                                :null
                              }
                              

                                {
                                q.question_type === "yes_no" ? 
                                <FormControl fullWidth sx={{pt: 1}}>
                                    <Stack spacing={1}>
                                    {
                                        [...Array(2)].map((item, idx) => (
                                            <TextField
                                                fullWidth
                                                size="small"
                                                label={`Choice ${idx + 1}`}
                                                value={q.choices[idx]?.choice_text || ''}
                                                onChange={(e) => setFieldValue(`questions[${index}].choices[${idx}].choice_text`, e.target.value)}
                                                />
                                        )) 
                                    }
                                    </Stack>
                                </FormControl>
                                :null
                              }
                            </Grid2>

                            <Grid2 item size={{xs:12}} sm="auto">
                              <Stack direction="row" spacing={1}>
                                <Tooltip title="Remove question" placement="top">
                                  <IconButton
                                    color="error"
                                    size="small"
                                    onClick={async() => {
                                      arrayHelpers.remove(index)
                                      values.remove_questions.push(q.id);
                                    }}
                                  >
                                    <DeleteIcon fontSize="small" />
                                  </IconButton>
                                </Tooltip>
                              </Stack>
                            </Grid2>
                          </Grid2>
                        </Paper>
                      );
                    })}

                    {touched.questions && typeof errors.questions === "string" && (
                      <Typography color="error" variant="body2">
                        {errors.questions}
                      </Typography>
                    )}
                  </Stack>
                )}
              />
            </DialogContent>

            <DialogActions sx={{ p: 2 }}>
              <Button onClick={() => onClose?.(null)}>Cancel</Button>
              <Button
                variant="contained"
                type="submit"
                disabled={isSubmitting}
              >
                {initialData ? "Save Changes" : "Create Survey"}
              </Button>
            </DialogActions>
          </Form>
        )}
      </Formik>
    </Dialog>
  );
};

export default AddSurvey;